import { Component, Input, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subscription, finalize, take } from 'rxjs';
import { LoadingService } from '../../../loading/loading.service';
import { EntitiesService } from 'src/app/modules/services/entities/entities.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ModalController } from '@ionic/angular';
import { ISortState } from './link-consorcio.interface';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { AlertService } from 'src/app/modules/services/alert/alert.service';
import { ToastService } from 'src/app/modules/services/toast/toast.service';

@Component({
  selector: 'app-link-consorcio-modal',
  templateUrl: './link-consorcio-modal.component.html',
  styleUrls: ['./link-consorcio-modal.component.scss'],
})
export class LinkConsorcioModalComponent {
  @Input() idEntity: string | null = null;
  form: FormGroup;
  entityName: string = '';
  subscriptions: Subscription[] = [];
  filteredConsorcios: any[] = [];
  selectedConsorcio: any = null;
  allConsorciosFromEntity: any[] = [];
  canSubmit: boolean = false;

  sortState: ISortState = { sortProperty: 'razaoSocial', sortDirection: 'asc' };
  pageSize: number = 10;
  pageIndex: number = 0;
  totalPages: number = 1;
  totalElements: number = 0;
  displayedColumns: string[] = [
    'razaoSocial',
    'lider',
    'cnpj',
    'participacao',
    'actions',
  ];

  dataSource = new MatTableDataSource<any>([]);
  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;

  constructor(
    private fb: FormBuilder,
    private loadingService: LoadingService,
    private toastService: ToastService,
    private entityService: EntitiesService,
    private modalCtrl: ModalController,
    private alertService: AlertService
  ) {
    this.form = this.fb.group({
      consorcioFilterInput: [''],
      participationFilterInput: [''],
      nameOrParticipationSearchTerm: [''],
    });
  }

  ngOnInit(): void {
    this.dataSource.paginator = this.paginator;
    if (this.idEntity) {
      this.loadEntityData(this.idEntity);
    }
  }

  loadEntityData(idEntity: string) {
    this.loadingService.showLoading();

    this.subscriptions.push(
      this.entityService
        .getEntityWithLinkedData(idEntity)
        .pipe(
          take(1),
          finalize(() => {
            this.loadingService.hideLoading();
          })
        )
        .subscribe(
          (response: any) => {
            this.dataSource.data = response.consorcios;
            this.allConsorciosFromEntity = response.consorcios;
            this.entityName = response.razaoSocial;
            this.setPaginatorValues();
          },
          (erro) => {
            if (erro.status !== 500 && erro.status) {
              this.toastService.showError(erro.error.message);
            } else {
              this.toastService.showError();
            }
          }
        )
    );
  }

  selectConsorcio(consorcio: any) {
    this.selectedConsorcio = consorcio;
    this.form.patchValue({ consorcioFilterInput: consorcio.razaoSocial });
    this.filteredConsorcios = [];
  }

  keydownSelectConsorcio(event: KeyboardEvent, consorcio: any) {
    if (
      event.key === 'Spacebar' ||
      event.key === 'Enter' ||
      event.key === ' '
    ) {
      event.preventDefault();
      this.selectConsorcio(consorcio);
    }
  }

  onAddSelectedConsorcio() {
    if (
      this.selectedConsorcio === null ||
      this.form.get('participationFilterInput')?.value === ''
    ) {
      return this.toastService.showError(
        `Adicione um consórcio e seu percentual de participação.`
      );
    }

    if (
      this.allConsorciosFromEntity.findIndex(
        (c) => c.idEmpresa === this.selectedConsorcio.id
      ) >= 0
    ) {
      return this.toastService.showError(
        `O consórcio "${this.selectedConsorcio?.razaoSocial}" já está vinculado.`
      );
    }

    let consorcios = this.dataSource.data;
    const newConsorcio = {
      idEmpresa: this.selectedConsorcio.id,
      razaoSocial: this.selectedConsorcio.razaoSocial,
      cnpj: this.selectedConsorcio.cnpj,
      participacao: this.form.get('participationFilterInput')?.value,
      lider: false,
    };
    consorcios.push(newConsorcio);
    this.updateLeader(consorcios);

    this.setPaginatorValues();
    this.form.patchValue({
      consorcioFilterInput: '',
      participationFilterInput: '',
    });
    this.selectedConsorcio = null;
    this.canSubmit = true;
  }

  applyConsorcioFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.data = this.allConsorciosFromEntity;

    if (filterValue) {
      let filteredConsorcios = this.dataSource.data;
      const lowerCaseSearchTerm = filterValue.toLowerCase();

      this.dataSource.data = filteredConsorcios.filter(
        (css) =>
          css.razaoSocial.toLowerCase().includes(lowerCaseSearchTerm) |
          css.cnpj.toLowerCase().includes(lowerCaseSearchTerm)
      );
    }

    this.setPaginatorValues();
  }

  onConsorcioSearch(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.selectedConsorcio = null;

    if (filterValue.length >= 3 || filterValue.length === 0) {
      this.consorcioSearch(filterValue);
    }
  }

  consorcioSearch(searchTerm: string) {
    if (!searchTerm) {
      return (this.filteredConsorcios = []);
    }

    return this.subscriptions.push(
      this.entityService
        .getEntitiesByCnpjRazaoSocial(searchTerm)
        .pipe(take(1))
        .subscribe(
          (response: any) => {
            this.filteredConsorcios = response.content.filter(
              (css: any) => css.id !== this.idEntity
            );
          },
          (erro) => {
            if (erro.status !== 500 && erro.status) {
              this.toastService.showError(erro.error.message);
            } else {
              this.toastService.showError();
            }
          }
        )
    );
  }

  onSortChange(sortProperty: 'razaoSocial' | 'participacao' | 'cnpj') {
    if (this.sortState.sortProperty === sortProperty) {
      this.sortState.sortDirection =
        this.sortState.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortState.sortProperty = sortProperty;
      this.sortState.sortDirection = 'asc';
    }

    const sortedUsers = this.dataSource.data.sort((a, b) => {
      let comparison = 0;
      if (a[sortProperty] > b[sortProperty]) {
        comparison = 1;
      } else if (a[sortProperty] < b[sortProperty]) {
        comparison = -1;
      }

      return this.sortState.sortDirection === 'asc' ? comparison : -comparison;
    });

    this.dataSource.data = sortedUsers;
  }

  onSortChangeKeydown(
    event: KeyboardEvent,
    sortProperty: 'razaoSocial' | 'participacao' | 'cnpj'
  ) {
    if (
      event.key === 'Spacebar' ||
      event.key === 'Enter' ||
      event.key === ' '
    ) {
      event.preventDefault();
      this.onSortChange(sortProperty);
    }
  }

  removeConsorcio(id: string) {
    const updatedConsorcios = this.dataSource.data.filter(
      (css) => css.idEmpresa !== id
    );
    this.dataSource.data = updatedConsorcios;
    this.allConsorciosFromEntity = updatedConsorcios;
    this.canSubmit = true;

    this.setPaginatorValues();
  }

  removeConsorcioKeydown(event: KeyboardEvent, id: string) {
    if (
      event.key === 'Spacebar' ||
      event.key === 'Enter' ||
      event.key === ' '
    ) {
      event.preventDefault();
      this.removeConsorcio(id);
    }
  }

  setPaginatorValues() {
    this.totalElements = this.dataSource.data.length;
    this.totalPages = Number(
      (1 + this.totalElements / this.pageSize).toPrecision(1)
    );
  }

  openLeaderChangeDialog(id: string) {
    this.alertService.showAlert(
      'Alterar liderança',
      'Você tem certeza que deseja tornar líder esse consórcio?',
      this.setLeader.bind(this),
      id
    );
  }

  openLeaderChangeDialogKeydown(event: KeyboardEvent, id: string) {
    if (
      event.key === 'Spacebar' ||
      event.key === 'Enter' ||
      event.key === ' '
    ) {
      event.preventDefault();
      this.openLeaderChangeDialog(id);
    }
  }

  setLeader(id: any): void {
    let updatedConsorcios = this.allConsorciosFromEntity.map((css) => ({
      ...css,
      lider: css.idEmpresa === id,
    }));

    this.allConsorciosFromEntity = updatedConsorcios;
    this.dataSource.data = updatedConsorcios;
    this.canSubmit = true;
  }

  updateLeader(consorcios: any): void {
    const leaderCss = consorcios.reduce((prev: any, current: any) =>
      prev.participacao > current.participacao ? prev : current
    );
    const updatedConsorcios = consorcios.map((css: any) => ({
      ...css,
      lider: css.idEmpresa === leaderCss.idEmpresa,
    }));

    this.allConsorciosFromEntity = updatedConsorcios;
    this.dataSource.data = updatedConsorcios;
  }

  cancel() {
    return this.modalCtrl.dismiss(null, 'cancel');
  }

  onSubmit() {
    this.loadingService.showLoading();

    const formData = {
      idEntidade: this.idEntity,
      consorcios: this.dataSource.data,
    };

    return this.subscriptions.push(
      this.entityService
        .linkConsorciosToEntity(formData)
        .pipe(
          take(1),
          finalize(() => {
            this.loadingService.hideLoading();
          })
        )
        .subscribe(
          (response: any) => {
            this.toastService.showSuccess(response.message);
            return this.modalCtrl.dismiss(null, 'confirm');
          },
          (erro) => {
            if (erro.status !== 500 && erro.status) {
              this.toastService.showError(erro.error.message);
            } else {
              this.toastService.showError();
            }
          }
        )
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
